import React, { useEffect, useState } from 'react';
import Page from '../../components/Page';
import { RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import MSHeader from '../../components/NonAuth/MSHeader';
import MSFooter from '../../components/NonAuth/MSFooter';
import NonAuthPage from '../../components/NonAuth/NonAuthPage';
import { useNavigate } from 'react-router-dom';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import PricingOption from '../../components/Pricing/PricingOption';
import {
  SubscriptionPlan,
  SubscriptionAccess,
  SubscriptionStatus,
} from '../../types/Subscription';
import {
  selectSubscription,
  setSubscription,
} from '../../slices/subscriptions/subscriptionsSlice';
import { getSubscriptionStatus } from '../../services/subscriptions';
import { Browser } from '@capacitor/browser';
import { useDarkMode } from '../../hooks/useDarkMode';
import posthog from 'posthog-js';
import _ from 'lodash';
import OraGuarantee from '../../assets/svgs/og.svg';
import { ArrowRight } from 'react-feather';
import { hapticsImpactLight } from '../../utils/haptics';

const Pricing: React.FC = () => {
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const subscription = useSelector(selectSubscription);

  const [showMarketingSiteStyles, setShowMarketingSiteStyles] = useState(false);
  const [toggleAnnualPricing, setToggleAnnualPricing] = useState(false);
  const [annualIsDefault, setAnnualIsDefault] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  useDarkMode();

  // Handle Capacitor Browser close
  useEffect(() => {
    const handleBrowserClose = async () => {
      const subscriptionData = await getSubscriptionStatus();
      if (subscriptionData) {
        dispatch(setSubscription(subscriptionData));
      }
      if (location.pathname !== '/') {
        navigate('/');
      }
    };

    Browser.addListener('browserFinished', handleBrowserClose);

    return () => {
      Browser.removeAllListeners();
    };
  }, []);

  useEffect(() => {
    if (
      currentUser &&
      !_.isNil(currentUser?.email) &&
      posthog.getFeatureFlag('pricing-page-default-plan') === 'annual'
    ) {
      setToggleAnnualPricing(true);
      setAnnualIsDefault(true);
    }
  }, [currentUser]);

  useEffect(() => {
    document.body.classList.add('pricing-page');
    if (isAuthenticated) {
      if (currentUser?.isOnboarded === false) {
        navigate('/onboarding');
      }
    }
    return () => {
      document.body.classList.remove('pricing-page');
    };
  }, [currentUser, isAuthenticated]);

  useEffect(() => {
    const referrer = document.referrer;

    const cameFromMarketingSite =
      referrer.startsWith('https://www.oraai.com') ||
      referrer.startsWith('http://www.oraai.com') ||
      referrer.startsWith('https://oraai.com') ||
      referrer.startsWith('http://oraai.com');

    if (cameFromMarketingSite) {
      setShowMarketingSiteStyles(true);
    }

    if (
      isAuthenticated &&
      subscription?.status === SubscriptionStatus.ACTIVE &&
      !cameFromMarketingSite
    ) {
      navigate('/account?sub=billing');
    }
  }, [subscription, isAuthenticated]);

  const renderPricing = () => {
    return (
      <div className="pricing">
        <div className="pricing_header">
          <h1 className="font-gradient">Med school can be simpler</h1>
          <ToggleButtonGroup
            value={toggleAnnualPricing ? 'annual' : 'monthly'}
            exclusive
            onChange={(_, value) => {
              setToggleAnnualPricing(value === 'annual');
            }}
            aria-label="Pricing period"
          >
            {!annualIsDefault && (
              <ToggleButton value="monthly" aria-label="monthly">
                Monthly
              </ToggleButton>
            )}
            <ToggleButton value="annual" aria-label="annual">
              Annual
            </ToggleButton>
            {annualIsDefault && (
              <ToggleButton value="monthly" aria-label="monthly">
                Monthly
              </ToggleButton>
            )}
          </ToggleButtonGroup>
        </div>
        <div className="pricing_body">
          <PricingOption
            isAuthenticated={isAuthenticated}
            product={SubscriptionAccess.FREE}
            plan={
              toggleAnnualPricing
                ? SubscriptionPlan.ANNUAL
                : SubscriptionPlan.MONTHLY
            }
            title="Basic"
            description="The standard Ora experience"
            monthlyPrice="0"
            annualPrice="0"
            features={[
              '25,000+ Flashcards for Step, Shelf, & School Exams',
              '1700+ Library Articles & 290 Preclinical Videos',
              '3 Questions per Day',
              'Limited Ora AI Autopilot',
            ]}
            savings=""
            savingPercent=""
            isGroupOption={false}
            guarantee={false}
          />
          <PricingOption
            isAuthenticated={isAuthenticated}
            product={SubscriptionAccess.PRE_CLINICAL}
            plan={
              toggleAnnualPricing
                ? SubscriptionPlan.ANNUAL
                : SubscriptionPlan.MONTHLY
            }
            title="Preclinical & Step 1"
            description="Great for MS1s and MS2s"
            monthlyPrice="20"
            annualPrice="10"
            features={[
              'Everything in Free',
              '6,000+ Step 1 & School-Specific Questions',
              'Timed QBank Mode',
              'Access to Ora AI Chat',
              'Full Ora AI Autopilot',
            ]}
            savings="$120 annual commitment, billed monthly"
            savingPercent="50%"
            isGroupOption={false}
            guarantee={true}
          />
          <PricingOption
            isAuthenticated={isAuthenticated}
            product={SubscriptionAccess.STEP_SHELF}
            plan={
              toggleAnnualPricing
                ? SubscriptionPlan.ANNUAL
                : SubscriptionPlan.MONTHLY
            }
            title="Ora Pro"
            description="Covers Preclinical, Step 1, Step 2CK, and Shelf"
            monthlyPrice="100"
            annualPrice="50"
            features={[
              'Everything in Free',
              'Everything in Preclinical & Step 1',
              '6,000+ Step 2CK Questions',
              '10,000+ NBME Shelf Questions',
            ]}
            savings="$600 annual commitment, billed monthly"
            savingPercent="50%"
            isGroupOption={false}
            guarantee={true}
          />
        </div>
        <div className="pricing_guarantee">
          <div className="pricing_guarantee_container" id="guarantee">
            <img
              className="ora-guarantee"
              src={OraGuarantee}
              alt="Ora Guarantee"
            />
            <h2>We'll Buy You UWorld If You Fail Step 1 or Step 2CK</h2>
            {/* <p>
              Many other services offer a money-back guarantee, but they often
              require you to fail the real exam first. This is ridiculous. We
              believe you shouldn't have to take chances with your time, money,
              or success.
            </p> */}
            <p>
              If you fail USMLE Step 1 or USMLE Step 2CK after using Ora, we'll
              buy you a <strong>one month subscription to UWorld.</strong>
            </p>
            {/* <p>
              If you fail a Step 1 or Step 2 practice test the week of your
              scheduled exam, we'll refund your entire subscription and buy you
              a <strong>one month subscription to UWorld.</strong>
            </p> */}
            <p>To be eligible, you must meet the following criteria:</p>
            <ul>
              <li>You are enrolled in a United States medical school.</li>
              <li>
                You have completed the Above Average (or higher) workload (QBank
                and flashcards) for Step 1 or Step 2CK.
              </li>
              <li>You fail Step 1 or Step 2CK.</li>
            </ul>
            <p>
              We want you to feel <strong>prepared and confident</strong> going
              into your exam. If you have any questions, please{' '}
              <button
                className="button button--link"
                style={{
                  display: 'inline !important',
                  padding: 0,
                  textDecoration: 'underline',
                }}
                onClick={() => {
                  hapticsImpactLight();
                  window.Intercom('show');
                }}
              >
                reach out to us!
              </button>
            </p>
            <div className="guarantee-footer">
              <div>
                <a
                  href="https://www.oraai.com/terms#ora-guarantee"
                  target="_blank"
                >
                  Read the full terms and conditions
                  <ArrowRight />
                </a>
              </div>
            </div>
          </div>
        </div>
        {/* <div className="pricing_footer">
          <button className="button button--link">
            Discounts are always available for low-income and URM students
            <ArrowRight />
          </button>
        </div> */}
      </div>
    );
  };

  return (
    <>
      {isAuthenticated && !showMarketingSiteStyles ? (
        <Page>{renderPricing()}</Page>
      ) : (
        <>
          <MSHeader />
          <NonAuthPage>{renderPricing()}</NonAuthPage>
          <MSFooter />
        </>
      )}
      {/* <div className="green-blur"></div> */}
    </>
  );
};

export default Pricing;
